import React from 'react';
import { Container, Typography, Box, Grid, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  Business,
  Public,
  TrendingUp,
  Group,
  Inventory,
  LocalShipping,
  SupportAgent,
  EmojiObjects,
  ThumbUp,
  MonetizationOn,
} from '@mui/icons-material';

const AmazonDropshippingProfile = () => {
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Company Name and Industry */}
        <Typography variant="h3" gutterBottom align="center" className="text-main-600 font-bold">
          Company Profile
        </Typography>
        <Typography variant="h5" gutterBottom align="center" sx={{ color: 'text.secondary' }}>
          Industry: E-Commerce | Website: amazondropshipping.com
        </Typography>

        {/* Overview Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Overview
        </Typography>
        <Typography variant="body1" paragraph>
          Amazon Dropshipping is a dynamic e-commerce business model that operates on the world’s leading online marketplace, Amazon. This model allows entrepreneurs to sell products to customers without holding inventory. The company sources products from third-party suppliers and directly ships them to customers, streamlining the order fulfillment process. Leveraging Amazon's massive global customer base and established reputation, Amazon Dropshipping provides a seamless shopping experience while focusing on high-quality products, exceptional customer service, and rapid fulfillment.
        </Typography>

        {/* Mission and Vision Section */}
        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h5" gutterBottom className="text-main-600 font-bold">
                <EmojiObjects sx={{ verticalAlign: 'middle', mr: 1, color: 'text-main-600' }} />
                Mission
              </Typography>
              <Typography variant="body1">
                To empower entrepreneurs with a scalable and sustainable e-commerce solution that enables them to build successful online businesses through Amazon’s trusted platform, offering a broad range of high-demand products with minimal upfront investment.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h5" gutterBottom className="text-main-600 font-bold">
                <Public sx={{ verticalAlign: 'middle', mr: 1, color: 'text-main-600' }} />
                Vision
              </Typography>
              <Typography variant="body1">
                To be the leading provider of dropshipping services on Amazon, enabling entrepreneurs of all backgrounds to thrive by offering unmatched product variety, strategic tools, and exceptional operational support.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Core Values Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Core Values
        </Typography>
        <Grid container spacing={4}>
          {coreValues.map((value, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Box sx={{ fontSize: '3rem', color: 'text-main-600' }}>
                  {value.icon}
                </Box>
                <Typography variant="h6" gutterBottom className="text-main-600 font-bold">
                  {value.title}
                </Typography>
                <Typography variant="body1">{value.description}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Business Model Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Business Model
        </Typography>
        <List>
          {businessModel.map((item, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <TrendingUp fontSize="small" sx={{ color: 'text-main-600' }} />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>

        {/* Product Categories Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Product Categories
        </Typography>
        <Grid container spacing={4}>
          {productCategories.map((category, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom className="text-main-600 font-bold">
                  {category}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Target Audience Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Target Audience
        </Typography>
        <List>
          {targetAudience.map((audience, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <Group fontSize="small" sx={{ color: 'text-main-600' }} />
              </ListItemIcon>
              <ListItemText primary={audience} />
            </ListItem>
          ))}
        </List>

        {/* Competitive Advantage Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Competitive Advantage
        </Typography>
        <Grid container spacing={4}>
          {competitiveAdvantages.map((advantage, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Box sx={{ fontSize: '3rem', color: 'text-main-600' }}>
                  {advantage.icon}
                </Box>
                <Typography variant="h6" gutterBottom className="text-main-600 font-bold">
                  {advantage.title}
                </Typography>
                <Typography variant="body1">{advantage.description}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Key Services Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Key Services
        </Typography>
        <List>
          {keyServices.map((service, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <LocalShipping fontSize="small" sx={{ color: 'text-main-600' }} />
              </ListItemIcon>
              <ListItemText primary={service} />
            </ListItem>
          ))}
        </List>

        {/* Conclusion Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Conclusion
        </Typography>
        <Typography variant="body1" paragraph>
          Amazon Dropshipping provides an accessible and profitable business opportunity for entrepreneurs looking to capitalize on the massive potential of the e-commerce industry. With a proven model, excellent customer service, and a seamless integration with Amazon’s platform, it offers the flexibility and support needed to build and grow a successful online business. Whether you’re new to e-commerce or an experienced seller, Amazon Dropshipping is an ideal solution to take your business to the next level.
        </Typography>
      </Box>
    </Container>
  );
};

// Core Values Data
const coreValues = [
  {
    icon: <EmojiObjects sx={{ color: 'text-main-600' }} />,
    title: 'Innovation',
    description: 'Continuously evolving with market trends to offer the most up-to-date solutions and products.',
  },
  {
    icon: <ThumbUp sx={{ color: 'text-main-600' }} />,
    title: 'Customer-Centric',
    description: 'Providing excellent customer service, hassle-free returns, and fast shipping to ensure satisfaction.',
  },
  {
    icon: <MonetizationOn sx={{ color: 'text-main-600' }} />,
    title: 'Integrity',
    description: 'Operating with transparency and honesty in every transaction and relationship.',
  },
  {
    icon: <TrendingUp sx={{ color: 'text-main-600' }} />,
    title: 'Efficiency',
    description: 'Optimizing business operations to reduce costs, improve delivery times, and maximize profits for entrepreneurs.',
  },
];

// Business Model Data
const businessModel = [
  'Product Sourcing: Connect sellers to reputable suppliers who manage production, packaging, and storage.',
  'Order Fulfillment: Suppliers directly ship products to customers upon order placement.',
  'Scalable Sales: Access to millions of Amazon users for increased visibility and sales potential.',
  'Marketing & Advertising: Utilize Amazon’s tools like Sponsored Products to drive traffic and sales.',
];

// Product Categories Data
const productCategories = [
  'Electronics and gadgets',
  'Fashion and accessories',
  'Home and kitchen products',
  'Health and beauty',
  'Toys and games',
  'Sports and outdoor gear',
  'Office supplies and stationery',
];

// Target Audience Data
const targetAudience = [
  'Entrepreneurs: Individuals looking to build an online business with minimal upfront capital.',
  'Small Business Owners: Those seeking to expand their product offerings on Amazon.',
  'Retailers: Businesses looking for reliable fulfillment methods to reduce operational costs.',
];

// Competitive Advantages Data
const competitiveAdvantages = [
  {
    icon: <Business sx={{ color: 'text-main-600' }} />,
    title: 'Low Overhead',
    description: 'No need for warehousing or inventory management, keeping operational costs minimal.',
  },
  {
    icon: <Public sx={{ color: 'text-main-600' }} />,
    title: 'Global Reach',
    description: 'Access to millions of potential customers worldwide through Amazon.',
  },
  {
    icon: <Inventory sx={{ color: 'text-main-600' }} />,
    title: 'Automated Systems',
    description: 'Easy automation of order fulfillment for scalable business growth.',
  },
  {
    icon: <ThumbUp sx={{ color: 'text-main-600' }} />,
    title: 'Reputation & Trust',
    description: 'Leverage Amazon’s credibility to increase sales and reduce customer hesitation.',
  },
];

// Key Services Data
const keyServices = [
  'Dropshipping Setup: Guidance for setting up a successful Amazon dropshipping store.',
  'Supplier Sourcing: Assistance in finding trustworthy suppliers with competitive prices.',
  'Inventory Management: No need to stock inventory; purchase products after customer orders.',
  'Customer Support Solutions: Dedicated support for inquiries, returns, and shipment issues.',
  'Market Research & Trend Analysis: Tools to identify profitable products and niche markets.',
];

export default AmazonDropshippingProfile;